import { Booking, Resource } from '@/types';
import {
  MatchSignal,
  ResourceMatchResult,
  RecommendationEngineInput,
  RecommendationEngineOutput,
  AvailablePeriod,
  ConflictDetail,
  UtilisationContext,
  DailyUtilisationDay,
} from '@/types/recommendation';
import type { RequestedSkill } from '@/types/resourceRequest';
import { calculatePeriodUtilization } from '@/utils/heatmap-utils';
import { consumesCapacity } from '@/lib/bookingCapacity';

// ---------------------------------------------------------------------------
// Role normalisation
// Normalise a role title so trivial formatting differences (case,
// surrounding/internal whitespace, punctuation) never cause a mismatch.
// e.g. "  Finance  Director " and "finance-director" both → "finance director".
// Matching is otherwise strict: only the exact normalised name is a match —
// there is no family/synonym or substring fallback (see computeRoleSignal).
// ---------------------------------------------------------------------------
function normaliseRole(role: string | undefined | null): string {
  return (role || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ') // strip punctuation to spaces
    .replace(/\s+/g, ' ')          // collapse internal whitespace
    .trim();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function isBookingForResource(booking: Booking, resourceId: string): boolean {
  if (booking.resourceId && String(booking.resourceId) === resourceId) return true;
  if (booking.resourceIds && booking.resourceIds.includes(resourceId)) return true;
  return false;
}

function dateOnlyMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Calendar days between two dates (inclusive of both endpoints) */
function calendarDaysBetween(a: Date, b: Date): number {
  return Math.max(1, Math.round(Math.abs(dateOnlyMs(b) - dateOnlyMs(a)) / 86400000) + 1);
}

// ---------------------------------------------------------------------------
// Hard filter: Availability
// Uses hour-level granularity so that partial-day bookings (e.g. 2 hours)
// don't mark the entire day as "fully booked".
// ---------------------------------------------------------------------------
const WORKING_HOURS_PER_DAY = 8;

export interface AvailabilityResult {
  isFullyBooked: boolean;
  availablePeriods: AvailablePeriod[];
  conflictDetails: ConflictDetail | null;
  explanation: string;
}

export function computeAvailabilitySignal(
  resource: Resource,
  startDate: Date,
  endDate: Date,
  existingBookings: Booking[]
): AvailabilityResult {
  // endDate is an inclusive calendar date (midnight of that day).
  // For overlap detection, treat it as exclusive by advancing to next day midnight.
  const exclusiveEnd = new Date(endDate);
  exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);

  const resourceBookings = existingBookings.filter(b =>
    isBookingForResource(b, resource.id) &&
    consumesCapacity(b) &&
    new Date(b.startTime) < exclusiveEnd &&
    new Date(b.endTime) > startDate
  );

  const startMs = dateOnlyMs(startDate);
  const endMs = dateOnlyMs(endDate);
  const totalDays = Math.max(1, Math.round((endMs - startMs) / 86400000) + 1);
  const totalHours = totalDays * WORKING_HOURS_PER_DAY;

  const periodStartMs = startDate.getTime();
  const periodEndMs = exclusiveEnd.getTime();
  let bookedHours = 0;
  for (const booking of resourceBookings) {
    const bStartMs = new Date(booking.startTime).getTime();
    const bEndMs = new Date(booking.endTime).getTime();
    const overlapStart = Math.max(bStartMs, periodStartMs);
    const overlapEnd = Math.min(bEndMs, periodEndMs);
    if (overlapEnd > overlapStart) {
      bookedHours += (overlapEnd - overlapStart) / 3600000;
    }
  }
  bookedHours = Math.min(bookedHours, totalHours);

  const freeHours = totalHours - bookedHours;
  const isFullyBooked = freeHours <= 0;

  let explanation: string;
  if (isFullyBooked) {
    explanation = 'Fully booked for this period';
  } else if (bookedHours === 0) {
    explanation = 'Free for the full booking period';
  } else {
    const freeRounded = Math.round(freeHours * 10) / 10;
    const totalRounded = Math.round(totalHours * 10) / 10;
    explanation = `Partially free — ${freeRounded}h of ${totalRounded}h available`;
  }

  const availablePeriods: AvailablePeriod[] = [];
  if (!isFullyBooked) {
    availablePeriods.push({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      type: bookedHours === 0 ? 'full' : 'partial',
    });
  }

  let conflictDetails: ConflictDetail | null = null;
  if (isFullyBooked && resourceBookings.length > 0) {
    const conflictBooking = resourceBookings[0];
    const conflictingResourceCount = conflictBooking.resourceIds
      ? conflictBooking.resourceIds.length
      : 1;
    const impactSummary =
      conflictingResourceCount === 1
        ? `Project ${conflictBooking.projectName || 'Unknown'} will be left with no resource`
        : `Project ${conflictBooking.projectName || 'Unknown'} will go from ${conflictingResourceCount} to ${conflictingResourceCount - 1} resources for this period`;

    conflictDetails = {
      conflictingBookingId: String(conflictBooking.id),
      conflictingProjectId: String(conflictBooking.projectId || ''),
      conflictingProjectName: conflictBooking.projectName || 'Unknown',
      allocationPercent: 100,
      overlapStart: conflictBooking.startTime,
      overlapEnd: conflictBooking.endTime,
      impactSummary,
      canMove: true,
      actionTaken: 'none',
    };
  }

  return {
    isFullyBooked,
    availablePeriods,
    conflictDetails,
    explanation,
  };
}

// ---------------------------------------------------------------------------
// Soft signal: Utilisation
// met = under-utilised (below target). Exposes utilisationHeadroom.
// ---------------------------------------------------------------------------
function computeUtilisationSignal(
  resource: Resource,
  startDate: Date,
  endDate: Date,
  existingBookings: Booking[],
  utilisationOverride?: number,
  utilisationContext?: UtilisationContext,
  utilisationDays?: DailyUtilisationDay[]
): MatchSignal & { utilisationHeadroom: number } {
  // Backend capacity data restricted to this period: authoritative bookings AND the
  // right window, so a single slot is not diluted by the whole request range.
  const windowed = utilisationDays && daysInWindow(utilisationDays, startDate, endDate);
  if (windowed && windowed.capacityHours > 0) {
    return buildUtilisationSignal(
      Math.round(Math.min(windowed.bookedHours / windowed.capacityHours, 1) * 100)
    );
  }

  if (utilisationOverride !== undefined) {
    return buildUtilisationSignal(Math.max(0, Math.min(100, Math.round(utilisationOverride))));
  }

  // Canonical planner/heatmap calculation: booked hours over the capacity that
  // remains once the working profile, bank holidays and absences are applied, with
  // hourly / half-day / full-day bookings each counted for their real duration.
  if (utilisationContext) {
    const { utilizationPercentage } = calculatePeriodUtilization(
      String(resource.id),
      startDate,
      endDate,
      existingBookings,
      resource,
      utilisationContext.globalWorkingProfile,
      utilisationContext.bankHolidays,
      utilisationContext.absences,
      utilisationContext.bookingTypes
    );
    return buildUtilisationSignal(utilizationPercentage);
  }

  const bookingDays = calendarDaysBetween(startDate, endDate);
  const halfWindow = Math.min(60, Math.max(7, Math.round(bookingDays / 2)));

  const windowStart = new Date(startDate);
  windowStart.setDate(windowStart.getDate() - halfWindow);
  const windowEnd = new Date(endDate);
  windowEnd.setDate(windowEnd.getDate() + halfWindow);

  const windowDays = Math.round((dateOnlyMs(windowEnd) - dateOnlyMs(windowStart)) / 86400000) + 1;
  const bookedDays = new Set<number>();
  const windowStartMs = dateOnlyMs(windowStart);

  for (const booking of existingBookings) {
    if (!isBookingForResource(booking, resource.id)) continue;
    if (!consumesCapacity(booking)) continue;
    const bStart = new Date(booking.startTime);
    const bEnd = new Date(booking.endTime);
    if (bEnd < windowStart || bStart > windowEnd) continue;

    let cur = dateOnlyMs(new Date(Math.max(bStart.getTime(), windowStart.getTime())));
    const capEnd = dateOnlyMs(new Date(Math.min(bEnd.getTime(), windowEnd.getTime())));
    while (cur <= capEnd) {
      bookedDays.add(Math.round((cur - windowStartMs) / 86400000));
      cur += 86400000;
    }
  }

  const utilisationPct = Math.round((bookedDays.size / windowDays) * 100);
  return buildUtilisationSignal(utilisationPct);
}

/** Totals the backend capacity/booked hours for the days inside [startDate, endDate]. */
function daysInWindow(
  days: DailyUtilisationDay[],
  startDate: Date,
  endDate: Date
): { capacityHours: number; bookedHours: number } {
  const from = toDateKey(startDate);
  const to = toDateKey(endDate);

  return days.reduce(
    (totals, day) => {
      if (day.date < from || day.date > to) return totals;
      return {
        capacityHours: totals.capacityHours + day.capacityHours,
        bookedHours: totals.bookedHours + day.bookedHours,
      };
    },
    { capacityHours: 0, bookedHours: 0 }
  );
}

function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

// Derives the utilisation signal (met / headroom / explanation) from a percentage,
// keeping the target threshold and wording in one place so a client-computed value
// and a backend-supplied override render identically.
function buildUtilisationSignal(utilisationPct: number): MatchSignal & { utilisationHeadroom: number } {
  const TARGET_UTILISATION = 80;
  const met = utilisationPct < TARGET_UTILISATION;
  const utilisationHeadroom = Math.max(0, TARGET_UTILISATION - utilisationPct);

  return {
    key: 'utilisation',
    label: 'Utilisation',
    met,
    explanation: met
      ? `${utilisationPct}% utilised — ${utilisationHeadroom}% headroom available`
      : `${utilisationPct}% utilised — at or above target capacity`,
    utilisationHeadroom,
  };
}

// ---------------------------------------------------------------------------
// Soft signal: Role
// met = exact normalised role-name equality only (strict). Role codes are not
// trusted (cross-namespace) and there is no family/synonym/substring fallback.
// ---------------------------------------------------------------------------
export function computeRoleSignal(
  resource: Resource,
  requiredRole: string | undefined,
  requiredRoleCode?: number | null,
): MatchSignal {
  const hasRoleName = !!requiredRole && requiredRole.trim() !== '';
  const hasRoleCode = requiredRoleCode !== undefined && requiredRoleCode !== null;

  if (!hasRoleName && !hasRoleCode) {
    return {
      key: 'role',
      label: 'Role',
      met: true,
      explanation: 'No role requirement specified',
    };
  }

  // Role "code" equality is deliberately NOT trusted as a match. The request's
  // `requiredRoleCode` originates from the EBProjects `Role` table (Demand.RoleId,
  // see demandAdapter.ts) while `resource.externalRoleId` originates from the PSA
  // Resources webhook `RoleId` (see database ResourceSyncService.cs). These are
  // two independent id namespaces with no mapping between them, so a numeric
  // coincidence (e.g. Projects role 42 vs PSA role 42) does not imply the same
  // role. Matching therefore relies solely on exact normalised role-name equality below.
  // Keep this behaviour in sync with backend/EBScheduler.Api/Helpers/RoleMatcher.cs.
  if (!hasRoleName) {
    return {
      key: 'role',
      label: 'Role',
      met: false,
      explanation: `Role mismatch — requires role code ${requiredRoleCode}, resource is ${resource.role || 'unspecified'}`,
    };
  }

  const req = normaliseRole(requiredRole);
  const res = normaliseRole(resource.role);

  // Strict match: only exact normalised name equality (case, whitespace and
  // punctuation insensitive) counts. No family/synonym or substring fallback.
  if (res && res === req) {
    return {
      key: 'role',
      label: 'Role',
      met: true,
      explanation: `Exact role match: ${resource.role}`,
    };
  }

  return {
    key: 'role',
    label: 'Role',
    met: false,
    explanation: `Role mismatch — requires ${requiredRole}, resource is ${resource.role || 'unspecified'}`,
  };
}

// ---------------------------------------------------------------------------
// Soft signal: Skills
// met = every REQUIRED skill covered (auto-pass when none is required —
// preferred skills never block a match). Exposes coverage of the required and
// the preferred skills separately, plus a combined percentage dominated by the
// required ones so ranking is required-first with preferred as a tiebreak.
// ---------------------------------------------------------------------------

export interface RequestedSkillsBreakdown {
  required: string[];
  preferred: string[];
}

/**
 * Split the requested skills into required and preferred names.
 *
 * Without the structured list (older data, or a caller that only has names)
 * every name is treated as required, preserving the previous behaviour.
 */
export function splitRequestedSkills(
  skillNames: string[] | undefined,
  requestedSkills?: RequestedSkill[],
): RequestedSkillsBreakdown {
  if (requestedSkills && requestedSkills.length > 0) {
    return {
      required: requestedSkills.filter(s => s.requirement === 'required').map(s => s.name),
      preferred: requestedSkills.filter(s => s.requirement !== 'required').map(s => s.name),
    };
  }
  return { required: [...(skillNames ?? [])], preferred: [] };
}

/** Percentage of `skillNames` the resource holds; 100 when none are asked for. */
export function skillsCoverageFor(resource: Resource, skillNames: string[]): number {
  if (skillNames.length === 0) return 100;
  const held = new Set((resource.skills ?? []).map(s => s.name.toLowerCase()));
  const matched = skillNames.filter(name => held.has(name.toLowerCase())).length;
  return Math.round((matched / skillNames.length) * 100);
}

/**
 * Weight the preferred skills carry in the combined coverage percentage when
 * required skills are also asked for. Small on purpose: a resource holding all
 * the required skills must always outrank one that only holds preferred ones.
 */
const PREFERRED_COVERAGE_WEIGHT = 0.1;

interface SkillsSignalResult extends MatchSignal {
  skillsCoveragePct: number;
  requiredSkillsCoveragePct: number;
  preferredSkillsCoveragePct: number;
}

function computeSkillsSignal(
  resource: Resource,
  requiredSkills: string[],
  requestedSkills?: RequestedSkill[],
): SkillsSignalResult {
  const { required, preferred } = splitRequestedSkills(requiredSkills, requestedSkills);

  if (required.length === 0 && preferred.length === 0) {
    return {
      key: 'skills',
      label: 'Skills',
      met: true,
      explanation: 'No skills requirement specified',
      skillsCoveragePct: 100,
      requiredSkillsCoveragePct: 100,
      preferredSkillsCoveragePct: 100,
    };
  }

  const requiredSkillsCoveragePct = skillsCoverageFor(resource, required);
  const preferredSkillsCoveragePct = skillsCoverageFor(resource, preferred);

  // Required coverage dominates; preferred coverage only nudges the figure so it
  // can break ties between resources with the same required coverage.
  const skillsCoveragePct = required.length === 0
    ? preferredSkillsCoveragePct
    : preferred.length === 0
      ? requiredSkillsCoveragePct
      : Math.round(
          requiredSkillsCoveragePct * (1 - PREFERRED_COVERAGE_WEIGHT) +
          preferredSkillsCoveragePct * PREFERRED_COVERAGE_WEIGHT,
        );

  // Preferred skills are a preference, not a condition: the signal is met as
  // soon as every required skill is held (and always met when none is required).
  const met = requiredSkillsCoveragePct === 100;

  return {
    key: 'skills',
    label: 'Skills',
    met,
    explanation: explainSkills(resource, required, preferred),
    skillsCoveragePct,
    requiredSkillsCoveragePct,
    preferredSkillsCoveragePct,
  };
}

function explainSkills(resource: Resource, required: string[], preferred: string[]): string {
  if ((resource.skills ?? []).length === 0) {
    const total = required.length + preferred.length;
    return `No skills recorded — 0 of ${total} requested skills matched`;
  }

  const held = new Set((resource.skills ?? []).map(s => s.name.toLowerCase()));
  const parts: string[] = [];

  if (required.length > 0) {
    const missing = required.filter(name => !held.has(name.toLowerCase()));
    parts.push(missing.length === 0
      ? `All ${required.length} required skills matched`
      : `${required.length - missing.length} of ${required.length} required skills matched — missing: ${listNames(missing)}`);
  }

  if (preferred.length > 0) {
    const matched = preferred.filter(name => held.has(name.toLowerCase())).length;
    parts.push(`${matched} of ${preferred.length} preferred skills matched`);
  }

  return parts.join('; ');
}

function listNames(names: string[]): string {
  return names.length <= 2
    ? names.join(', ')
    : `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

// ---------------------------------------------------------------------------
// Main engine
// ---------------------------------------------------------------------------
export function computeRecommendations(
  input: RecommendationEngineInput
): RecommendationEngineOutput {
  const {
    startDate,
    endDate,
    requiredRole,
    requiredRoleCode,
    requiredSkills,
    requestedSkills,
    allResources,
    existingBookings,
    utilisationByResourceId,
    utilisationDaysByResourceId,
    utilisationContext,
    utilisationPeriod,
  } = input;

  if (!startDate || !endDate) {
    return { cleanMatches: [], conflictMatches: [], computedAt: Date.now() };
  }

  const cleanMatches: ResourceMatchResult[] = [];
  const conflictMatches: ResourceMatchResult[] = [];

  const scorableResources = allResources.filter(r => r.id !== 'unallocated');

  for (const resource of scorableResources) {
    const availabilityResult = computeAvailabilitySignal(resource, startDate, endDate, existingBookings);
    const utilisationResult  = computeUtilisationSignal(
      resource,
      utilisationPeriod?.start ?? startDate,
      utilisationPeriod?.end ?? endDate,
      existingBookings,
      utilisationByResourceId?.[String(resource.id)],
      utilisationContext,
      utilisationDaysByResourceId?.[String(resource.id)],
    );
    const roleSignal         = computeRoleSignal(resource, requiredRole, requiredRoleCode);
    const skillsResult       = computeSkillsSignal(resource, requiredSkills || [], requestedSkills);

    // The skills signal is only a criterion when the request actually asks for
    // skills. Otherwise it is omitted entirely (rather than auto-passed) so the
    // UI never presents a skills match the requester never asked for, and the
    // score reads "N of 2".
    const signals: MatchSignal[] = [
      { key: utilisationResult.key, label: utilisationResult.label, met: utilisationResult.met, explanation: utilisationResult.explanation },
      roleSignal,
    ];
    if ((requiredSkills?.length ?? 0) > 0 || (requestedSkills?.length ?? 0) > 0) {
      signals.push({ key: skillsResult.key, label: skillsResult.label, met: skillsResult.met, explanation: skillsResult.explanation });
    }

    const softSignalsMet = signals.filter(s => s.met).length;
    const skillsCoveragePct = skillsResult.skillsCoveragePct;
    const utilisationHeadroom = utilisationResult.utilisationHeadroom;

    const availabilityStatus: ResourceMatchResult['availabilityStatus'] =
      availabilityResult.isFullyBooked
        ? 'conflict'
        : availabilityResult.availablePeriods.some(p => p.type === 'partial')
        ? 'partial'
        : 'free';

    const result: ResourceMatchResult = {
      resource,
      softSignalsMet,
      skillsCoveragePct,
      requiredSkillsCoveragePct: skillsResult.requiredSkillsCoveragePct,
      preferredSkillsCoveragePct: skillsResult.preferredSkillsCoveragePct,
      utilisationHeadroom,
      signals,
      availabilityStatus,
      availablePeriods: availabilityResult.availablePeriods,
      conflictDetails: availabilityResult.conflictDetails,
    };

    if (availabilityResult.isFullyBooked) {
      conflictMatches.push(result);
    } else {
      cleanMatches.push(result);
    }
  }

  // Sort: softSignalsMet desc, required-then-preferred skills coverage desc,
  // utilisationHeadroom desc
  const sortFn = (a: ResourceMatchResult, b: ResourceMatchResult) => {
    if (b.softSignalsMet !== a.softSignalsMet) return b.softSignalsMet - a.softSignalsMet;
    if (b.requiredSkillsCoveragePct !== a.requiredSkillsCoveragePct) {
      return b.requiredSkillsCoveragePct - a.requiredSkillsCoveragePct;
    }
    if (b.skillsCoveragePct !== a.skillsCoveragePct) return b.skillsCoveragePct - a.skillsCoveragePct;
    return b.utilisationHeadroom - a.utilisationHeadroom;
  };

  cleanMatches.sort(sortFn);
  conflictMatches.sort(sortFn);

  return {
    cleanMatches,
    conflictMatches,
    computedAt: Date.now(),
  };
}
